import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, cn } from "./primitives";

// ---------------------------------------------------------------------------
// Confirm dialog: replaces window.confirm (foreign look, shows the page address in an
// iOS standalone PWA, cannot follow sunlight mode or the dark theme).

export interface ConfirmOptions {
  /** Short heading naming the action ("Storno računa"). */
  title: React.ReactNode;
  /** The question and its consequence. */
  message?: React.ReactNode;
  /** Defaults to the provider's label (e.g. "Potvrdi"). Name the action: "Storniraj", "Obriši". */
  confirmLabel?: string;
  /** Defaults to the provider's label (e.g. "Odustani"). */
  cancelLabel?: string;
  /** Removes data or cannot be undone: danger confirm button and initial focus on cancel. */
  destructive?: boolean;
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  /** Cancel button, Escape, click outside. */
  onCancel: () => void;
  /** Dock (phone, sunlight mode): bottom sheet with large full-width buttons. */
  dock?: boolean;
}

export function ConfirmDialog({ open, title, message, confirmLabel, cancelLabel, destructive, dock, onConfirm, onCancel }: ConfirmDialogProps) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const confirmRef = React.useRef<HTMLButtonElement>(null);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const cancel = (
    <Button key="cancel" ref={cancelRef} variant="outline" size={dock ? "dock" : "md"} className={dock ? "w-full" : undefined} onClick={onCancel}>
      {cancelLabel}
    </Button>
  );
  const confirm = (
    <Button
      key="confirm"
      ref={confirmRef}
      variant={destructive ? "danger" : "primary"}
      size={dock ? "dock" : "md"}
      className={cn(dock && "w-full", destructive && "bg-danger-tint")}
      onClick={onConfirm}
    >
      {confirmLabel}
    </Button>
  );
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogPrimitive.Portal>
        {/* above regular dialogs (z-50): a confirm may be asked from inside one */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[55] bg-black/50" />
        <DialogPrimitive.Content
          role="alertdialog"
          aria-modal="true"
          data-surface={dock ? "dock" : undefined}
          {...(message ? {} : { "aria-describedby": undefined })}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            (destructive ? cancelRef : confirmRef).current?.focus();
          }}
          onCloseAutoFocus={(e) => {
            // no Radix trigger: return focus to whatever opened the dialog, if it is still there
            e.preventDefault();
            const el = returnFocus.current;
            returnFocus.current = null;
            if (el?.isConnected) el.focus();
          }}
          className={cn(
            "fixed z-[56] flex flex-col bg-surface text-ink shadow-xl outline-none",
            dock
              ? "inset-x-0 bottom-0 mx-auto w-full max-w-[560px] gap-4 rounded-t-card border-2 border-b-0 border-primary px-5 pt-6 pb-[max(20px,env(safe-area-inset-bottom))]"
              : "left-1/2 top-1/2 w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 gap-3 rounded-card border border-border p-5",
          )}
        >
          <DialogPrimitive.Title className={cn("m-0 font-extrabold", dock ? "text-22" : "text-20")}>{title}</DialogPrimitive.Title>
          {message ? <DialogPrimitive.Description className={cn("m-0", dock ? "text-17" : "text-15")}>{message}</DialogPrimitive.Description> : null}
          {/* dock: action on top, cancel at the bottom (thumb reach); office: cancel, then action on the right */}
          <div className={cn("flex gap-3", dock ? "mt-1 flex-col" : "mt-2 flex-wrap justify-end gap-2")}>{dock ? [confirm, cancel] : [cancel, confirm]}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------
// useConfirm(): `if (!(await confirm({ title, message, destructive: true }))) return;`

export interface ConfirmRequest extends ConfirmOptions {
  id: number;
}

export interface ConfirmStore {
  /** Open the dialog; resolves true on confirm, false on cancel, Escape or click outside. */
  request: (options: ConfirmOptions) => Promise<boolean>;
  /** Answer the open request (no-op when none is open). */
  settle: (ok: boolean) => void;
  current: () => ConfirmRequest | null;
  subscribe: (listener: () => void) => () => void;
}

/** One question at a time: a newer request answers a still-open one with `false`. */
export function createConfirmStore(): ConfirmStore {
  let current: (ConfirmRequest & { resolve: (ok: boolean) => void }) | null = null;
  let seq = 0;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((l) => l());
  return {
    request(options) {
      const previous = current;
      return new Promise<boolean>((resolve) => {
        current = { ...options, id: ++seq, resolve };
        previous?.resolve(false);
        emit();
      });
    },
    settle(ok) {
      const c = current;
      if (!c) return;
      current = null;
      emit();
      c.resolve(ok);
    },
    current: () => current,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const ConfirmContext = React.createContext<ConfirmStore["request"] | null>(null);

export function ConfirmProvider({
  children,
  confirmLabel,
  cancelLabel,
  dock,
  store: storeProp,
}: {
  children: React.ReactNode;
  /** Default labels (translated by the app). */
  confirmLabel: string;
  cancelLabel: string;
  dock?: boolean;
  /** For tests. */
  store?: ConfirmStore;
}) {
  const [own] = React.useState(createConfirmStore);
  const store = storeProp ?? own;
  const req = React.useSyncExternalStore(store.subscribe, store.current, store.current);
  // leaving the tree (e.g. logout) answers an open question with "no"
  React.useEffect(() => () => store.settle(false), [store]);
  return (
    <ConfirmContext.Provider value={store.request}>
      {children}
      <ConfirmDialog
        open={!!req}
        title={req?.title ?? ""}
        message={req?.message}
        confirmLabel={req?.confirmLabel ?? confirmLabel}
        cancelLabel={req?.cancelLabel ?? cancelLabel}
        destructive={req?.destructive}
        dock={dock}
        onConfirm={() => store.settle(true)}
        onCancel={() => store.settle(false)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmStore["request"] {
  const confirm = React.useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm outside ConfirmProvider");
  return confirm;
}
